import { AxiosError } from 'axios';
import React, { Component, ErrorInfo, ReactNode, useState } from 'react';

import { isAxiosError } from '~/utils/Axios';
import { ErrorXXXPortal } from './Pages/errorXXX';

interface Props {
  children: ReactNode;
}

type ErrorWithExtra<T = {}> = Error & T;

interface ErrorCatcherProps {
  onError(error: ErrorWithExtra, errorInfo: ErrorInfo): void;
  children?: React.ReactNode;
}

class ErrorCatcher extends Component<ErrorCatcherProps> {
  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('Uncaught error:', error, errorInfo);

    this.props.onError(error, errorInfo);
  }

  public render() {
    return this.props.children ?? null;
  }
}

interface AxiosErrorXXXProps {
  error: AxiosError;
}

function AxiosErrorXXX({ error }: AxiosErrorXXXProps) {
  const { message, response } = error;

  // We do not want to handle Aborted Request error from AXIOS
  // This happens when an API is called via axios then user redirects to another page while waiting for response
  // If handled this displays an error page quickly before going to the new page/state
  const requestWasAborted = message == 'Request aborted';

  if (response && response.status >= 500 && !requestWasAborted) {
    return (
      <ErrorXXXPortal title={response.statusText} httpCode={response.status} />
    );
  }

  return null;
}

interface ErrorObject {
  error: ErrorWithExtra;
  errorInfo: ErrorInfo;
}

export default function ErrorBoundaryProvider({ children }: Props) {
  const [errorObject, setErrorObject] = useState<ErrorObject>();

  if (errorObject?.error) {
    const { error } = errorObject;

    if (isAxiosError(error)) {
      // Access Token is expired
      if (error.response?.status === 401) {
        window.location.reload();

        return children;
      }

      return <AxiosErrorXXX error={error} />;
    } else {
      const error = errorObject?.error as ErrorWithExtra<{ number: number }>;

      return <ErrorXXXPortal httpCode={error.number} />;
    }
  }

  return (
    <ErrorCatcher
      onError={(error, errorInfo) => setErrorObject({ error, errorInfo })}
    >
      {children}
    </ErrorCatcher>
  );
}
